import React from 'react';
import { useSelector } from 'react-redux';
import { MILLION_BATH } from '../../common/helpers/constants';
import { handleImageUrl } from '../../common/helpers/sharpie/imageUrlHelper';
import { getProjectPagePath } from '../../common/helpers/routing/urlGenerator';
import commonResource from '../../locales/th/common.json';
import { WholeState } from '../../store/common/type';
import NoImagePlaceHolder from '../card/component/NoImagePlaceHolder';
import { projectCardLogoConfig, projectImageConfig } from '../card/config';
import LazyImage from '../lazyImage/LazyImage';
import {
  Address,
  CardImageWrapper,
  CardInfoPanel,
  CardLogo,
  CardLogoWrapper,
  CardTitle,
  CardWrapper,
  Developer,
  PriceInfo,
  PropertyInfo,
} from './SimpleProjectCard.style';

interface Props {
  id: number;
  title: string;
  featuredImageUrl: string;
  link: string;
  location: {
    district: { label: string }[];
    road: { label: string }[];
    zone: { label: string }[];
  };
  priceTypes: {
    starting_price: string;
    display_price?: string;
    level: {
      value: string;
      label: string;
    };
  };
  propertyTypes: string[];
  developerName: string;
  logoUrl: string;
  logoBgColor: string;
  transportations: {
    value: string;
    label: string;
    link: string;
  }[];
  onClickProjectCard?: (event: ProjectCardClickEvent) => void;
}

export interface ProjectCardClickEvent {
  id: number;
  title: string;
  location: {
    district: { label: string }[];
    road: { label: string }[];
    zone: { label: string }[];
  };
  priceTypes: {
    starting_price: string;
    display_price?: string;
    level: {
      value: string;
      label: string;
    };
  };
  propertyTypes: string[];
  developerName: string;
  transportations: {
    value: string;
    label: string;
    link: string;
  }[];
}

function SimpleProjectCard(props: Props): React.ReactElement {
  const {
    id,
    featuredImageUrl,
    title,
    link,
    location,
    propertyTypes,
    developerName = '',
    logoUrl,
    logoBgColor,
    priceTypes,
    transportations,
    onClickProjectCard,
  } = props;

  const zone = location.zone || [];
  const startingPrice = priceTypes.starting_price;
  const displayPrice = priceTypes.display_price;

  const deviceType = useSelector((state: WholeState) =>
    state.common !== undefined ? state.common.deviceType : 'desktop',
  );

  const isNextGenSupported = useSelector<WholeState, boolean>((state) =>
    state.common !== undefined ? state.common.isNextGenSupported : false,
  );

  const addressText = zone.map((z) => z.label).join(', ');
  const propertyTypeText = propertyTypes.join(', ');

  const projectPageLink = getProjectPagePath(link);

  const getSharpieUrl = (imgUrl: string) => {
    const { width, height } = projectImageConfig[deviceType];
    return handleImageUrl(imgUrl, width, height, isNextGenSupported);
  };

  const displayPriceText =
    displayPrice || startingPrice
      ? `${commonResource.simpleCard.startingPrice}  ${
          displayPrice || `${startingPrice} ${MILLION_BATH}`
        }`
      : '';

  const cardImage = featuredImageUrl ? (
    <LazyImage src={getSharpieUrl(featuredImageUrl)} alt={title} />
  ) : (
    <NoImagePlaceHolder />
  );

  return (
    <a
      href={projectPageLink}
      onClick={() =>
        onClickProjectCard({
          id,
          propertyTypes,
          developerName,
          title,
          location,
          priceTypes,
          transportations,
        })
      }
    >
      <CardWrapper>
        <CardImageWrapper>{cardImage}</CardImageWrapper>
        {logoUrl && (
          <CardLogoWrapper>
            <CardLogo
              bgColor={logoBgColor}
              src={handleImageUrl(
                logoUrl,
                projectCardLogoConfig.width,
                projectCardLogoConfig.height,
                isNextGenSupported,
                'fit',
              )}
            />
          </CardLogoWrapper>
        )}
        <CardInfoPanel>
          <CardTitle>{title}</CardTitle>
          <Address>{addressText}</Address>
          <PropertyInfo>{propertyTypeText}</PropertyInfo>
          <PriceInfo>{displayPriceText}</PriceInfo>
          <Developer>{developerName}</Developer>
        </CardInfoPanel>
      </CardWrapper>
    </a>
  );
}

export default SimpleProjectCard;
